import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Calculate as CalculateIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  CloudUpload as SubmitIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Types
interface VATReturn {
  period_start: string;
  period_end: string;
  vat_due_sales: number;
  vat_due_acquisitions: number;
  total_vat_due: number;
  vat_reclaimed: number;
  net_vat_due: number;
  total_value_sales: number;
  total_value_purchases: number;
  total_value_goods_supplied: number;
  total_acquisitions: number;
  vat_due_sales_formatted: string;
  vat_due_acquisitions_formatted: string;
  total_vat_due_formatted: string;
  vat_reclaimed_formatted: string;
  net_vat_due_formatted: string;
  total_value_sales_formatted: string;
  total_value_purchases_formatted: string;
  sales_lines: VATReturnLine[];
  purchase_lines: VATReturnLine[];
}

interface VATReturnLine {
  description: string;
  net_amount: number;
  vat_rate: string;
  vat_amount: number;
  total_amount: number;
  net_amount_formatted: string;
  vat_amount_formatted: string;
  total_amount_formatted: string;
}

interface VATSummary {
  period_start: string;
  period_end: string;
  sales_vat: number;
  purchase_vat: number;
  net_vat_due: number;
  return_due_date: string;
  status: string;
}

const VATReturns: React.FC = () => {
  const [vatReturn, setVatReturn] = useState<VATReturn | null>(null);
  const [vatSummary, setVatSummary] = useState<VATSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [periodStart, setPeriodStart] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1));
  const [periodEnd, setPeriodEnd] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchVATData();
  }, [periodStart, periodEnd]);

  const fetchVATData = async () => {
    try {
      setLoading(true);

      // Fetch VAT return data
      const startDate = periodStart.toISOString().split('T')[0];
      const endDate = periodEnd.toISOString().split('T')[0];

      const [returnResponse, summaryResponse] = await Promise.all([
        fetch(`/api/v1/reports/vat-return?start_date=${startDate}&end_date=${endDate}`),
        fetch('/api/v1/dashboard/vat-summary')
      ]);

      if (returnResponse.ok) {
        const returnData = await returnResponse.json();
        setVatReturn(returnData);
      }

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setVatSummary(summaryData);
      }
    } catch (error) {
      console.error('Error fetching VAT data:', error);
      setSnackbar({ open: true, message: 'Failed to load VAT data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReturn = async () => {
    try {
      setSubmitting(true);

      // In a real implementation, this would submit to HMRC via MTD API
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call

      setSnackbar({ open: true, message: 'VAT return submitted successfully to HMRC!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to submit VAT return', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = async () => {
    try {
      if (!vatReturn) return;

      setSubmitting(true);
      const response = await fetch('/api/cfo/hmrc/pay-vat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: vatReturn.net_vat_due,
          return_url: window.location.href + "?status=success",
          back_url: window.location.href + "?status=cancelled"
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.nextUrl) {
          window.location.href = data.nextUrl;
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to initiate HMRC payment', severity: 'error' });
      }
    } catch (error) {
      console.error("Payment Error:", error);
      setSnackbar({ open: true, message: 'Payment error', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'default';
      case 'submitted': return 'success';
      case 'overdue': return 'error';
      default: return 'info';
    }
  };

  const formatVATBox = (boxNumber: number, label: string, value: string, description: string) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" color="primary">
              Box {boxNumber}: {label}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>VAT Returns</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            VAT Returns - Making Tax Digital
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<SubmitIcon />}
              onClick={handleSubmitReturn}
              disabled={submitting}
              color="success"
            >
              {submitting ? 'Submitting...' : 'Submit to HMRC'}
            </Button>
          </Box>
        </Box>

        {/* Period Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              VAT Period
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Period Start"
                  value={periodStart}
                  onChange={(date) => setPeriodStart(date || new Date())}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Period End"
                  value={periodEnd}
                  onChange={(date) => setPeriodEnd(date || new Date())}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<CalculateIcon />}
                  onClick={fetchVATData}
                  fullWidth
                >
                  Recalculate
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                {vatSummary && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Due Date: {new Date(vatSummary.return_due_date).toLocaleDateString('en-GB')}
                    </Typography>
                    <Chip
                      label={vatSummary.status.toUpperCase()}
                      color={getStatusColor(vatSummary.status) as any}
                      size="small"
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* MTD Compliance Status */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Making Tax Digital (MTD) Compliance:</strong> This system is designed to meet HMRC's MTD requirements.
            All calculations are performed automatically from your transaction data.
          </Typography>
        </Alert>

        {vatReturn && (
          <Grid container spacing={3}>
            {/* VAT Return Boxes */}
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                VAT Return - {new Date(vatReturn.period_start).toLocaleDateString('en-GB')} to {new Date(vatReturn.period_end).toLocaleDateString('en-GB')}
              </Typography>

              {/* Box 1-5: VAT Calculations */}
              {formatVATBox(1, "VAT due on sales", vatReturn.vat_due_sales_formatted, "VAT charged on your sales and other outputs")}
              {formatVATBox(2, "VAT due on acquisitions", vatReturn.vat_due_acquisitions_formatted, "VAT due on acquisitions from other EU Member States")}
              {formatVATBox(3, "Total VAT due", vatReturn.total_vat_due_formatted, "Total of Box 1 and Box 2")}
              {formatVATBox(4, "VAT reclaimed", vatReturn.vat_reclaimed_formatted, "VAT reclaimed on purchases and other inputs")}

              <Card sx={{ mb: 2, bgcolor: vatReturn.net_vat_due >= 0 ? 'error.light' : 'success.light' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6" color="primary">
                        Box 5: Net VAT {vatReturn.net_vat_due >= 0 ? 'due' : 'refund'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {vatReturn.net_vat_due >= 0 ? 'Amount to pay to HMRC' : 'Amount HMRC will refund to you'}
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" color={vatReturn.net_vat_due >= 0 ? 'error.main' : 'success.main'}>
                      {vatReturn.net_vat_due_formatted}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Box 6-9: Turnover Figures */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>Turnover and Other Outputs</Typography>

              {formatVATBox(6, "Total value of sales", vatReturn.total_value_sales_formatted, "Total value of sales and all other outputs excluding VAT")}
              {formatVATBox(7, "Total value of purchases", vatReturn.total_value_purchases_formatted, "Total value of purchases and all other inputs excluding VAT")}
              {formatVATBox(8, "Total value of goods supplied", `£${(vatReturn.total_value_goods_supplied || 0).toFixed(2)}`, "Total value of all supplies of goods to other EU Member States")}
              {formatVATBox(9, "Total acquisitions", vatReturn.vat_due_acquisitions_formatted, "Total value of acquisitions of goods from other EU Member States")}
            </Grid>

            {/* Summary and Actions */}
            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Return Summary
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Period:</Typography>
                    <Typography variant="body2">
                      {new Date(vatReturn.period_start).toLocaleDateString('en-GB')} - {new Date(vatReturn.period_end).toLocaleDateString('en-GB')}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Sales Transactions:</Typography>
                    <Typography variant="body2">{vatReturn.sales_lines.length}</Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Purchase Transactions:</Typography>
                    <Typography variant="body2">{vatReturn.purchase_lines.length}</Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography variant="h6">Net VAT:</Typography>
                    <Typography variant="h6" color={vatReturn.net_vat_due >= 0 ? 'error.main' : 'success.main'}>
                      {vatReturn.net_vat_due_formatted}
                    </Typography>
                  </Box>

                  {vatSummary && (
                    <Alert
                      severity={vatReturn.net_vat_due >= 0 ? 'warning' : 'success'}
                      action={
                        vatReturn.net_vat_due >= 0 ? (
                          <Button
                            color="inherit"
                            size="small"
                            onClick={handlePayNow}
                            disabled={submitting}
                            sx={{ fontWeight: 'bold' }}
                          >
                            {submitting ? 'Connecting...' : 'PAY NOW'}
                          </Button>
                        ) : null
                      }
                    >
                      <Typography variant="body2">
                        {vatReturn.net_vat_due >= 0
                          ? `Payment due by ${new Date(vatSummary.return_due_date).toLocaleDateString('en-GB')}`
                          : 'Refund will be processed by HMRC'
                        }
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* MTD Submission Status */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    MTD Submission
                  </Typography>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    This return will be submitted directly to HMRC via the Making Tax Digital API.
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      fullWidth
                    >
                      Preview Return
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                    >
                      Download Backup
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default VATReturns;
